import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { TextField, Alert, Typography, Box, useTheme, useMediaQuery, styled, FormControlLabel } from "__barrel_optimize__?names=Alert,Box,FormControlLabel,TextField,Typography,styled,useMediaQuery,useTheme!=!@mui/material";
import { tokens } from "../theme";
import { useTranslation } from "../../../i18n/client";
import ReusableButton from "./ReusableButton";
import { signIn, useSession } from "next-auth/react";
import { Trans } from "react-i18next";
import { AuthApi } from "../../../../apis/auth";
const StyledFormControlLabel = styled(FormControlLabel)({
    "& .MuiFormControlLabel-label": {
        color: "#515151",
        fontWeight: "500",
        fontSize: "13px"
    }
});
export const VerifyForm = (props)=>{
    const [verificationCode, setVerificationCode] = useState("");
    var _props_initialMessage;
    const [message, setMessage] = useState((_props_initialMessage = props.initialMessage) !== null && _props_initialMessage !== void 0 ? _props_initialMessage : "");
    var _props_initialMessageType;
    const [messageType, setMessageType] = useState((_props_initialMessageType = props.initialMessageType) !== null && _props_initialMessageType !== void 0 ? _props_initialMessageType : "info");
    const [emailError, setEmailError] = useState(false);
    const [repeatPasswordError, setRepeatPasswordError] = useState(false);
    const [checked, setChecked] = useState(false);
    const [pressedResend, setPressedResend] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const router = useRouter();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { data: session, status } = useSession();
    const { t } = useTranslation(props.lng, "signup");
    const searchParams = useSearchParams();
    const search = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get("callbackUrl");
    const [timeLeft, setTimeLeft] = useState(60);
    useEffect(()=>{
        // Only start the countdown if resendIsPresent is false
        if (!pressedResend || timeLeft === 0) return;
        const timerId = setInterval(()=>{
            setTimeLeft((prevTime)=>prevTime - 1);
        }, 1000);
        // Clear interval when the component unmounts or timeLeft changes
        return ()=>clearInterval(timerId);
    }, [
        pressedResend,
        timeLeft
    ]);
    // Reset countdown when it ends
    useEffect(()=>{
        if (timeLeft === 0) {
            setPressedResend(false); // Enable the resend button or message
            setTimeLeft(60); // Reset the countdown time if needed
        }
    }, [
        timeLeft,
        pressedResend
    ]);
    const handleSignIn = async (email, password)=>{
        const response = await signIn("credentials", {
            email,
            password,
            redirect: false
        });
        if (response === null || response === void 0 ? void 0 : response.error) {
            setMessage(response === null || response === void 0 ? void 0 : response.error);
            setMessageType("error");
        } else {
            if (search) {
                router.push("".concat(search));
            } else {
                router.push("/application");
            }
        }
    };
    const handleSubmit = async (event)=>{
        event.preventDefault();
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        setMessage("");
        try {
            await AuthApi.verifyCode({
                email: props.email,
                code: verificationCode
            });
            await handleSignIn(props.email, props.password);
        } catch (error) {
            setMessage(t("verification_error"));
            setMessageType("error");
        } finally{
            setIsSubmitting(false);
        }
    };
    const handleChangeVerificationCode = (event)=>{
        const nextValue = event.target.value.replace(/\D/g, "");
        setVerificationCode(nextValue);
    };
    const handleResendVerificationCode = async ()=>{
        try {
            await AuthApi.sendVerificationCode({
                email: props.email,
                locale: props.lng
            });
            setPressedResend(true);
        } catch (error) {
            setMessage(t("resend_verification_error"));
            setMessageType("error");
        }
    };
    const helperText = (resendIsPresend)=>{
        if (resendIsPresend) {
            return /*#__PURE__*/ _jsx("span", {
                children: /*#__PURE__*/ _jsx(Trans, {
                    i18nKey: "send_code",
                    t: t,
                    values: {
                        tleft: timeLeft
                    }
                })
            });
        } else {
            return /*#__PURE__*/ _jsxs("span", {
                children: [
                    " ",
                    t("code_valid"),
                    " ",
                    /*#__PURE__*/ _jsx("span", {
                        onClick: handleResendVerificationCode,
                        style: {
                            cursor: "pointer",
                            textDecoration: "underline"
                        },
                        children: t("resend")
                    })
                ]
            });
        }
    };
    return /*#__PURE__*/ _jsx(Box, {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        sx: {
            backgroundColor: "#F3F5FD"
        },
        children: /*#__PURE__*/ _jsxs(Box, {
            width: "100%",
            maxWidth: isMobile ? 320 : 501,
            p: "30px",
            borderRadius: "20px",
            sx: {
                backgroundColor: "#FFF"
            },
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h3",
                    component: "h1",
                    style: {
                        textAlign: "center",
                        marginBottom: "20px"
                    },
                    children: t("confirm_email")
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    component: "h1",
                    style: {
                        textAlign: "center",
                        marginBottom: "20px"
                    },
                    children: t("confirm_statement")
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit,
                    style: {
                        width: "100%"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "Code",
                            type: "text",
                            inputMode: "numeric",
                            autoComplete: "one-time-code",
                            value: verificationCode,
                            onChange: handleChangeVerificationCode,
                            variant: "outlined",
                            margin: "none",
                            fullWidth: true,
                            required: true,
                            helperText: helperText(pressedResend)
                        }),
                        /*#__PURE__*/ _jsx(ReusableButton, {
                            name: t("confirm"),
                            backgroundColor: colors.button.main,
                            backgroundColorOnHover: colors.button.mainOnHower,
                            type: "submit",
                            color: "#FFF",
                            width: "100%",
                            paddingTop: "20px",
                            loading: isSubmitting
                        }),
                        message && /*#__PURE__*/ _jsx(Alert, {
                            severity: messageType,
                            style: {
                                marginTop: "20px"
                            },
                            children: message
                        })
                    ]
                })
            ]
        })
    });
};
